/**
 * Where an argument comes from, picked from one searchable list: the AI, a fixed value, or any field
 * of a step it may wait for. It makes the same choices a wire does, for a field easier found by name.
 */

import { useRef, type ReactNode } from 'react';
import { Check, PenLine, Sparkles, X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';
import type { CustomToolArg, CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../../builder/builder-context';
import { candidateSources, fixedValueStart, withAiInput, withArg, type CustomToolDraft } from '../../custom-tool-draft';
import { outputPaths } from '../../output-paths';
import { useStepName } from '../../steps/step-name';
import { useCanvas } from '../canvas-context';
import { OutputPath } from './output-path';

const Picked = ({ when }: { when: boolean }) => (when ? <Check className='ml-auto size-3.5 text-indigo-600' aria-label='Current' /> : null);

interface PickerOptionsProps {
	step: CustomToolStep;
	field: ToolField;
	/** Set before a fixed value is chosen, so the new text box keeps the focus when the picker closes. */
	onFixedValue: () => void;
}

/** The choices, built only while the picker is open: they read every other step's output fields. */
const PickerOptions = ({ step, field, onFixedValue }: PickerOptionsProps) => {
	const { draft, change, stepTools } = useCustomToolBuilder();
	const { setPicking } = useCanvas();
	const stepName = useStepName();
	const arg = step.args[field.name];
	const sources = candidateSources(draft, step.id);
	const sameNameInput = draft.inputs.some((input) => input.name === field.name);

	const choose = (edit: (draft: CustomToolDraft) => CustomToolDraft) => {
		change(edit);
		setPicking(null);
	};
	const set = (next: CustomToolArg | null) => choose((current) => withArg(current, step.id, field.name, next));

	return (
		<Command>
			<CommandInput placeholder={`Where ${field.name} comes from`} className='text-[13px]' />
			<CommandList>
				<CommandEmpty className='py-6 text-center text-xs text-gray-800'>No field matches.</CommandEmpty>
				<CommandGroup heading='The AI passes it'>
					{!sameNameInput && (
						<CommandItem value={`new input ${field.name}`} onSelect={() => choose((current) => withAiInput(current, step.id, field))} className='gap-2'>
							<Sparkles className='size-3.5 text-sky-600' aria-hidden />
							<span className='text-[13px]'>
								New input <span className='font-mono text-sky-800'>{field.name}</span>
							</span>
						</CommandItem>
					)}
					{draft.inputs.map((input) => (
						<CommandItem key={input.name} value={`input ${input.name}`} onSelect={() => set({ source: 'input', input: input.name })} className='gap-2'>
							<Sparkles className='size-3.5 text-sky-600' aria-hidden />
							<span className='font-mono text-[13px] text-sky-800'>{input.name}</span>
							<Picked when={arg?.source === 'input' && arg.input === input.name} />
						</CommandItem>
					))}
				</CommandGroup>
				<CommandGroup heading='Fixed value'>
					<CommandItem
						value='type a value'
						keywords={['fixed value']}
						onSelect={() => {
							onFixedValue();
							set(fixedValueStart(field));
						}}
						className='gap-2'
					>
						<PenLine className='size-3.5 text-gray-800' aria-hidden />
						<span className='text-[13px]'>Type a value</span>
						<Picked when={arg?.source === 'value'} />
					</CommandItem>
				</CommandGroup>
				{sources.map((source) => {
					const paths = outputPaths(stepTools.toolFor(source)?.outputSchema);
					if (!paths.length) return null;
					return (
						<CommandGroup key={source.id} heading={stepName(source)}>
							{paths.map((path) => (
								<CommandItem key={path} value={`${source.id} ${path}`} onSelect={() => set({ source: 'step', step: source.id, path })} className='gap-2'>
									<OutputPath path={path} />
									<Picked when={arg?.source === 'step' && arg.step === source.id && arg.path === path} />
								</CommandItem>
							))}
						</CommandGroup>
					);
				})}
				{arg && (
					<CommandGroup>
						<CommandItem value='clear' onSelect={() => set(null)} className='gap-2'>
							<X className='size-3.5 text-gray-800' aria-hidden />
							<span className='text-[13px]'>Clear</span>
						</CommandItem>
					</CommandGroup>
				)}
			</CommandList>
		</Command>
	);
};

interface ArgPickerProps {
	step: CustomToolStep;
	field: ToolField;
	/** The trigger: the argument's slot on the card. */
	children: ReactNode;
}

export const ArgPicker = ({ step, field, children }: ArgPickerProps) => {
	const { picking, setPicking } = useCanvas();
	// A fixed value swaps the slot for a text box, which takes the focus instead of the gone trigger.
	const toValue = useRef(false);

	return (
		<Popover
			open={picking?.stepId === step.id && picking.field === field.name}
			onOpenChange={(open) => setPicking(open ? { stepId: step.id, field: field.name } : null)}
		>
			<PopoverTrigger asChild>{children}</PopoverTrigger>
			<PopoverContent
				align='start'
				side='right'
				className='w-80 p-0'
				onCloseAutoFocus={(event) => {
					if (toValue.current) event.preventDefault();
					toValue.current = false;
				}}
			>
				<PickerOptions step={step} field={field} onFixedValue={() => (toValue.current = true)} />
			</PopoverContent>
		</Popover>
	);
};
